/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { map } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import { Header, Mixins } from '@blend/enterprise-components';

import { LedgerRowActionConfig, generateRowActionComponents } from '../../utils/ledgerUtils';
import { LedgerHeader } from './LedgerHeader';
import { LedgerRow } from './LedgerRow';

const styles = StyleSheet.create({
  headerBar: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  ledgerRowWrapper: {
    display: 'flex',
    position: 'relative',
  },
  tableAction: {
    ':last-child': {
      marginRight: 8,
    },
    paddingTop: 6,
  },
  tableActionsWrapper: {
    ...Mixins.marginVertical(8),
  },
});

export interface LedgerSubsectionProps {
  columnWidths: number[];
  headerConfig?: {
    rightComponent?: React.ReactNode;
    size: number;
    title: string;
  };
  ledgerActions?: React.ReactNode[];
  ledgerHeaderComponents?: React.ReactNode[];
  ledgerRowActionsConfig?: LedgerRowActionConfig[];
  ledgerRowConfigs: {
    banner?: React.ReactNode;
    columns: React.ReactNode[];
    disableRowActions: boolean;
    hideRow?: boolean;
    overwritingAction?: React.ReactNode;
  }[];
}

export const LedgerSubsection: React.FC<LedgerSubsectionProps> = (props: LedgerSubsectionProps) => {
  const {
    ledgerHeaderComponents,
    ledgerRowConfigs,
    headerConfig,
    columnWidths,
    ledgerRowActionsConfig = [],
    ledgerActions = [],
  } = props;
  const rowActionColumnWidths = map(ledgerRowActionsConfig, ({ columnWidth }) => columnWidth);

  return (
    <React.Fragment>
      {headerConfig && (
        <div className={css(styles.headerBar)}>
          <Header fontWeight="BOLD" size={headerConfig.size}>
            {headerConfig.title}
          </Header>
          <div>{headerConfig.rightComponent || null}</div>
        </div>
      )}

      <LedgerHeader
        columnWidths={columnWidths}
        headerComponents={ledgerHeaderComponents}
        rowActionHeaders={{ columnWidths: rowActionColumnWidths }}
      />

      {map(ledgerRowConfigs, (ledgerRowConfig, rowIdx) => {
        if (ledgerRowConfig.hideRow) return false;
        const rowActionComponents = generateRowActionComponents(
          ledgerRowActionsConfig,
          rowIdx,
          ledgerRowConfig.disableRowActions,
        );

        return (
          <div className={css(styles.ledgerRowWrapper)} data-it={`LedgerSubsectionRow-${rowIdx}`} key={rowIdx}>
            <LedgerRow
              banner={ledgerRowConfig.banner}
              columnWidths={columnWidths}
              columns={ledgerRowConfig.columns}
              overwritingAction={ledgerRowConfig.overwritingAction}
              rowActions={{
                actions: rowActionComponents,
                columnWidths: rowActionColumnWidths,
              }}
            />
          </div>
        );
      })}

      <div className={css(styles.tableActionsWrapper)}>
        {ledgerActions.map((tableAction, idx) => (
          <div className={css(styles.tableAction)} key={idx}>
            {tableAction}
          </div>
        ))}
      </div>
    </React.Fragment>
  );
};
